import ArticleCard from './components/ArticleCard';
import ArticleCardSkeleton from './components/ArticleCardSkeleton';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import TrendingArticles from './components/TrendingArticles';
import { BsBookmark } from 'react-icons/bs';
import { BookmarksProvider } from './BookmarksContext';
import { ArticleEngagementProvider } from './ArticleEngagementContext';
import { ArticleFlagProvider } from './ArticleFlagContext';
import { FilterPill, FollowablePill } from '../components/common';
import {
  DateFilterSelect,
  SearchField,
  Select,
  SortSelect,
} from '../components/ui';
import { FilterSidebar, FilterSection } from '../components/layout';
import { Helmet } from 'react-helmet-async';
import { useInfiniteArticles } from './hooks/useInfiniteArticles';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { useDebounce } from './hooks/useDebounce';
import LanguageSelector from './components/LanguageSelector';
import { useLocaleContext } from '../client/contexts/LocaleContext';
import { NEWS_PROVIDER_FILTER_OPTIONS } from '../shared/newsProviders';
import { useAuth } from 'wasp/client/auth';
import { updateFollowedCategories } from 'wasp/client/operations';
import {
  ENGLISH_AVAILABLE_CATEGORIES,
  getCategoryDisplayName,
} from './jobs/generatedCode/types/index';

type SortOption = 'newest' | 'oldest' | 'mostPositive' | 'leastPositive';

export default function ArticlesPage() {
  return (
    <BookmarksProvider>
      <ArticleEngagementProvider>
        <ArticleFlagProvider>
          <>
            <Helmet>
              <link
                rel="canonical"
                href="https://optimistic-news.com/articles"
              />
              <title>Positive News Articles - Optimistic News</title>
              <meta
                name="description"
                content="Read uplifting and inspiring news articles curated daily. Stay informed with positive perspectives on world events, technology, science, and human progress."
              />
              <meta
                property="og:title"
                content="Positive News Articles - Optimistic News"
              />
              <meta
                property="og:description"
                content="Read uplifting and inspiring news articles curated daily. Stay informed with positive perspectives on world events, technology, science, and human progress."
              />
              <meta
                property="og:url"
                content="https://optimistic-news.com/articles"
              />
            </Helmet>
            <ArticlesPageContent />
          </>
        </ArticleFlagProvider>
      </ArticleEngagementProvider>
    </BookmarksProvider>
  );
}

function ArticlesPageContent() {
  const { locale } = useLocaleContext();
  const { data: user } = useAuth();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<'all' | '1d' | '1w' | '1m'>(
    'all',
  );
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    articles: allArticles,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useInfiniteArticles(30, {
    locale,
    bookmarksOnly: showBookmarksOnly,
    dateFilter,
    provider: selectedProvider,
    categories: selectedCategories,
    searchQuery: debouncedSearchQuery,
    sortBy,
  });

  const infiniteScrollRef = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
  });

  const sourceArticles = useMemo(
    () => (Array.isArray(allArticles) ? allArticles : []),
    [allArticles],
  );

  const uniqueCategories = useMemo(() => {
    return ENGLISH_AVAILABLE_CATEGORIES.map((en) =>
      getCategoryDisplayName(en, locale),
    );
  }, [locale]);

  const followedCategories = useMemo(
    () => (user?.followedCategories as string[]) ?? [],
    [user?.followedCategories],
  );

  // Seed selected category filters from followed categories on initial load only
  const hasSeededFilters = useRef(false);
  useEffect(() => {
    if (hasSeededFilters.current) return;
    if (followedCategories.length === 0) return;
    hasSeededFilters.current = true;
    const displayNames = followedCategories.map((en) =>
      getCategoryDisplayName(en, locale),
    );
    setSelectedCategories((prev) => (prev.length > 0 ? prev : displayNames));
  }, [followedCategories, locale]);

  const getEnglishCategory = useCallback(
    (displayCategory: string) => {
      if (locale === 'de') {
        return (
          ENGLISH_AVAILABLE_CATEGORIES.find(
            (en) => getCategoryDisplayName(en, 'de') === displayCategory,
          ) ?? displayCategory
        );
      }
      return displayCategory;
    },
    [locale],
  );

  // Track optimistic follow state to prevent race conditions on rapid toggles.
  // The ref is only synced from auth cache when no mutations are in-flight.
  const pendingFollows = useRef(followedCategories);
  const inflightMutations = useRef(0);
  useEffect(() => {
    if (inflightMutations.current === 0) {
      pendingFollows.current = followedCategories;
    }
  }, [followedCategories]);

  const toggleFollow = useCallback(
    (category: string) => {
      const englishCategory = getEnglishCategory(category);
      const current = pendingFollows.current;
      const isCurrentlyFollowed = current.includes(englishCategory);
      const next = isCurrentlyFollowed
        ? current.filter((c) => c !== englishCategory)
        : [...current, englishCategory];
      pendingFollows.current = next;
      inflightMutations.current += 1;
      updateFollowedCategories({ followedCategories: next })
        .catch(() => {
          // Undo only this specific toggle rather than restoring a stale snapshot,
          // so concurrent toggles aren't clobbered.
          const latest = pendingFollows.current;
          pendingFollows.current = isCurrentlyFollowed
            ? [...latest, englishCategory]
            : latest.filter((c) => c !== englishCategory);
        })
        .finally(() => {
          inflightMutations.current -= 1;
        });
      // Auto-select as filter when following
      if (!isCurrentlyFollowed) {
        setSelectedCategories((prev) =>
          prev.includes(category) ? prev : [...prev, category],
        );
      }
    },
    [getEnglishCategory],
  );

  const { forYouArticles, everythingElseArticles } = useMemo(() => {
    if (followedCategories.length === 0) {
      return {
        forYouArticles: [] as typeof sourceArticles,
        everythingElseArticles: sourceArticles,
      };
    }
    const forYou = sourceArticles.filter((article) =>
      (article.englishCategories || []).some((cat: string) =>
        followedCategories.includes(cat),
      ),
    );
    const rest = sourceArticles.filter(
      (article) =>
        !(article.englishCategories || []).some((cat: string) =>
          followedCategories.includes(cat),
        ),
    );
    return { forYouArticles: forYou, everythingElseArticles: rest };
  }, [sourceArticles, followedCategories]);

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'mostPositive', label: 'Most Positive' },
    { value: 'leastPositive', label: 'Least Positive' },
  ];

  // ArticlesPageContent:onReset — App-wide locale excluded from reset — stays in
  // LocaleContext, not managed by sidebar filters. Sentiment > 1 base filter is
  // server-side (getBaseArticleWhere) and unreachable by client-side reset.
  const handleReset = () => {
    setSelectedCategories([]);
    setDateFilter('all');
    setSortBy('newest');
    setSelectedProvider('all');
    setShowBookmarksOnly(false);
  };

  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: '1d', label: 'Last 24 Hours' },
    { value: '1w', label: 'Last Week' },
    { value: '1m', label: 'Last Month' },
  ];

  const sidebarFilters = (
    <>
      <FilterSection heading="Categories">
        <div className="flex flex-wrap gap-1.5">
          <FilterPill
            label="All"
            isSelected={selectedCategories.length === 0}
            onClick={() => setSelectedCategories([])}
          />
          {uniqueCategories.map((category) => {
            const englishCategory = getEnglishCategory(category);
            return (
              <FollowablePill
                key={category}
                label={category}
                isSelected={selectedCategories.includes(category)}
                onClick={() => toggleCategory(category)}
                isFollowed={followedCategories.includes(englishCategory)}
                onFollowToggle={() => toggleFollow(category)}
                isAuthenticated={!!user}
              />
            );
          })}
        </div>
      </FilterSection>

      <FilterSection heading="Bookmarks">
        <FilterPill
          label="Bookmarks"
          isSelected={showBookmarksOnly}
          onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
          className="flex items-center gap-2"
        >
          <BsBookmark />
          Bookmarks
        </FilterPill>
      </FilterSection>

      <FilterSection heading="Language">
        <LanguageSelector />
      </FilterSection>

      <FilterSection heading="Date">
        <DateFilterSelect
          value={dateFilter}
          onChange={(value) => setDateFilter(value as typeof dateFilter)}
          options={dateFilterOptions}
          aria-label="Filter by date"
        />
      </FilterSection>

      <FilterSection heading="Provider">
        <Select
          value={selectedProvider}
          onChange={setSelectedProvider}
          options={NEWS_PROVIDER_FILTER_OPTIONS}
          aria-label="Filter by provider"
        />
      </FilterSection>

      <FilterSection heading="Sort">
        <SortSelect
          value={sortBy}
          onChange={(value) => setSortBy(value as SortOption)}
          options={sortOptions}
          aria-label="Sort articles"
        />
      </FilterSection>
    </>
  );

  if (isLoading) {
    return (
      <div className="py-6 lg:py-10">
        {/* Loading skeletons in sidebar layout */}
        <FilterSidebar
          sidebarLabel="Filters"
          onReset={handleReset}
          content={
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 lg:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <ArticleCardSkeleton key={i} />
              ))}
            </div>
          }
        >
          {sidebarFilters}
        </FilterSidebar>
      </div>
    );
  }

  if (error)
    return (
      <div className="text-center mt-8 text-danger-500">
        Error: {error.message}
      </div>
    );

  const showPartitions =
    followedCategories.length > 0 && forYouArticles.length > 0;

  const articleGrid = (
    <>
      {sourceArticles.length === 0 && !isLoading && (
        <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
          No articles found matching your criteria
        </div>
      )}

      {showPartitions ? (
        <>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
            For You
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 lg:gap-8 items-stretch">
            {forYouArticles.map((article) => (
              <div key={article.id} className="h-full">
                <ArticleCard article={article} />
              </div>
            ))}
          </div>

          {everythingElseArticles.length > 0 && (
            <>
              <div className="my-8 border-t border-neutral-200 dark:border-neutral-700" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                Everything Else
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 lg:gap-8 items-stretch">
                {everythingElseArticles.map((article) => (
                  <div key={article.id} className="h-full">
                    <ArticleCard article={article} />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 lg:gap-8 items-stretch">
          {sourceArticles &&
            Array.isArray(sourceArticles) &&
            sourceArticles.map((article) => (
              <div key={article.id} className="h-full">
                <ArticleCard article={article} />
              </div>
            ))}
        </div>
      )}

      {isLoadingMore && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-6 lg:gap-8 items-stretch mt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <ArticleCardSkeleton key={`loading-${i}`} />
          ))}
        </div>
      )}

      {hasMore && <div ref={infiniteScrollRef} className="h-4 mt-8"></div>}
    </>
  );

  return (
    <div className="py-6 lg:py-10">
      {/* Trending Articles Section */}
      {/* ArticlesPageContent:trendingArticleIds — Trending IDs exclusion preserved
          from original — prevents duplication with TrendingArticles section */}
      {!showBookmarksOnly && <TrendingArticles locale={locale} />}

      {/* Search Section — remains above the sidebar+content grid (DL-005) */}
      <div className="mb-8 px-4 sm:px-0">
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search articles..."
          className="mx-auto max-w-xl"
        />
      </div>

      {/* Sidebar + content grid */}
      <FilterSidebar
        sidebarLabel="Filters"
        onReset={handleReset}
        content={articleGrid}
      >
        {sidebarFilters}
      </FilterSidebar>
    </div>
  );
}
